// Externals
import { useSelector } from 'react-redux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { ShowroomSectionKey, WorkflowStatus } from '@makemydeal/dr-sales-checkin-types';
import type { SectionScrollRequest, ShowroomNavState } from './types';
import { fullScrollWorkflowRouteMap, workflowRouteMap } from '../workflow/workflowRouteMap';
import type { SalesCheckinRootState } from '../common/types/SalesCheckinRootState';

export const useShowroomNavConfigSelector = (): ShowroomNavState => {
    const showroomNav: ShowroomNavState = useSelector((state: SalesCheckinRootState) => state.salesCheckin.showroomNav || {});
    return showroomNav;
};

export const getActiveSectionKey = (state: SalesCheckinRootState): ShowroomSectionKey | undefined =>
    state.salesCheckin?.showroomNav?.activeSectionKey;

export const getSectionScrollRequest = (state: SalesCheckinRootState): SectionScrollRequest | undefined =>
    state.salesCheckin?.showroomNav?.sectionScrollRequest;

/**
 * Whether the Build Your Offer sections render as one scrollable page (F304636).
 *
 * The single reader of the toggle for navigation and rendering: the route table, the nav handlers,
 * the sidebar highlight and the loading telemetry all go through this, so the toggle's name appears
 * once and the cleanup when it retires is one file.
 */
export const isFullPageScrollEnabled = (state: SalesCheckinRootState): boolean =>
    featureToggleSelectors.isSciFullPageScrollEnabled(state as unknown as StateTree);

/**
 * The status→route map for the experience in play. Under the toggle every section status resolves to
 * the one-page address, which is what keeps the workflow push on the page instead of bouncing the
 * user to a standalone section screen.
 */
export const getWorkflowRouteMap = (state: SalesCheckinRootState): Record<WorkflowStatus, string> =>
    isFullPageScrollEnabled(state) ? fullScrollWorkflowRouteMap : workflowRouteMap;
